<script setup lang="ts">
import type { CustomerActiveSubscription } from '@entities/customer'

import { faChevronDown, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'

import { SubscriptionRowActions } from '@widgets/subscriptions'
import { BadgeSubscriptionStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTooltip } from '@shared/ui/tooltips'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppIcon } from '@shared/ui/icons'

import { useCustomerSubscriptionCard } from '../model/customer-subscription-card.hook'

defineOptions({
  name: 'CustomerSubscriptionCard',
})

const props = defineProps<{
  subscription: CustomerActiveSubscription
  canManage: boolean
}>()

const {
  refIsOpen,
  refSubscriptionUrl,
  refPausedUntil,
  refPlannedPause,
  refType,
  refKids,
  refPace,
  refDetails,
  toggle,
} = useCustomerSubscriptionCard({
  subscription: computed(() => props.subscription),
})
</script>

<template>
  <div class="customer-subscription-card">
    <div class="customer-subscription-card__actions">
      <SubscriptionRowActions
        :subscription="subscription"
        :can-manage="canManage"
        :with-details="false"
        with-tasks
      />
    </div>

    <div class="customer-subscription-card__row">
      <AppText
        class="customer-subscription-card__label"
        name="body1"
        color="slate-500"
      >
        Subscription
      </AppText>
      <span class="customer-subscription-card__value">
        <LinkTextPrimary
          :href="refSubscriptionUrl"
          target="_blank"
        >
          #{{ subscription.id }}
        </LinkTextPrimary>
        <BadgeSubscriptionStatus :status="subscription.status" />
        <AppText
          v-if="refPausedUntil"
          name="body1"
          color="amber-600"
        >
          {{ refPausedUntil }}
        </AppText>
        <AppTooltip
          v-if="refPlannedPause"
          :text="refPlannedPause"
          size="small"
          placement="top"
        >
          <AppIcon
            :icon="faTriangleExclamation"
            size="small"
            color="amber-500"
          />
        </AppTooltip>
      </span>
    </div>
    <div class="customer-subscription-card__row">
      <AppText
        class="customer-subscription-card__label"
        name="body1"
        color="slate-500"
      >
        Type
      </AppText>
      <AppText name="body1">
        {{ refType }}
      </AppText>
    </div>
    <div class="customer-subscription-card__row">
      <AppText
        class="customer-subscription-card__label"
        name="body1"
        color="slate-500"
      >
        {{ refKids.length > 1 ? 'Kids' : 'Kid' }}
      </AppText>
      <span class="customer-subscription-card__value">
        <template
          v-for="(kid, index) in refKids"
          :key="kid.id"
        >
          <LinkTextPrimary :href="kid.url">
            {{ kid.name }}
          </LinkTextPrimary>
          <AppText
            v-if="index < refKids.length - 1"
            name="body1"
          >
            ,
          </AppText>
        </template>
      </span>
    </div>
    <div class="customer-subscription-card__row">
      <AppText
        class="customer-subscription-card__label"
        name="body1"
        color="slate-500"
      >
        Auto-renew
      </AppText>
      <AppText
        name="body1-medium"
        :color="subscription.auto_renew ? 'emerald-600' : 'red-600'"
      >
        {{ subscription.auto_renew ? 'Enabled' : 'Disabled' }}
      </AppText>
    </div>
    <div class="customer-subscription-card__row">
      <AppText
        class="customer-subscription-card__label"
        name="body1"
        color="slate-500"
      >
        Pace
      </AppText>
      <AppText name="body1">
        {{ refPace }}
      </AppText>
    </div>

    <template v-if="refIsOpen">
      <div
        v-for="row in refDetails"
        :key="row.label"
        class="customer-subscription-card__row"
      >
        <AppText
          class="customer-subscription-card__label"
          name="body1"
          color="slate-500"
        >
          {{ row.label }}
        </AppText>
        <AppText name="body1">
          {{ row.value }}
        </AppText>
      </div>
    </template>

    <button
      type="button"
      :class="['customer-subscription-card__toggle', { 'customer-subscription-card__toggle--open': refIsOpen }]"
      :aria-expanded="refIsOpen"
      @click="toggle"
    >
      <AppIcon
        :icon="faChevronDown"
        size="small"
        color="slate-500"
      />
    </button>
  </div>
</template>

<style scoped lang="scss" src="./CustomerSubscriptionCard.scss" />
